import React from 'react';
import RenderIf from '@src/components/RenderIf/RenderIf';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import Translate from '@src/components/Translate/Translate';
import {UserContext} from '@src/contexts/UserContext';
import {IReduxModelConfigsState} from '@src/@interfaces';
import {ModelViewProps} from '@src/@types/components/ModelView';
import app from '@src/helpers/app';
import PageTitle from '@src/components/PageMetaTags/PageTitle';
import trans from '@src/helpers/trans';
import config from '@src/helpers/config';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {IImportConfig} from '@src/@interfaces/components/Import/IImportConfig';
import ModelView from '@src/components/_views/configurable/ModelView';
import FileReceiver from '@src/components/_views/configurable/import-view/file-receiver/FileReceiver';

type ResolveProps<Base, Model extends IEmptyModel> = Omit<ModelViewProps<Model>, 'listProps'> & Base;

export class ImportView<
        Model extends IEmptyModel = IEmptyModel,
        Props extends Record<any, any> = Record<any, any>,
> extends ModelView<ResolveProps<Props, Model>, IImportConfig> {
    static configsContainerName: keyof Pick<IReduxModelConfigsState, 'importConfigs'> = 'importConfigs';

    protected renderUnconnectedToStoreException(message: string, exampleOfConnection: string | null = null) {
        exampleOfConnection ??= `ImportView.getStoreConnector()(ImportView)`;

        return super.renderUnconnectedToStoreException(message, exampleOfConnection);
    }

    protected renderContent(): React.ReactNode {
        return <UserContext.Consumer>{userContext => {
            const permissions = this.getAccessPermissions();
            const accessDenied = !userContext?.canAny(permissions);

            return this.renderCard(accessDenied);
        }}</UserContext.Consumer>;
    }

    protected renderCard(accessDenied: boolean) {
        const errorHasOccurred = !this.getConfig() && !this.nowLoading();
        if (errorHasOccurred) {
            return <>
                <PageTitle title={trans('Exceptions.not_found') + ' | ' + config('client.app-name')}/>
                <Translate
                        message="Exceptions.configuration.model-form-configuration-file-not-found"
                        className="alert alert-danger py-5 text-center"
                        tag="div"
                />
            </>;
        }

        return this.renderCardContent(accessDenied);
    }

    protected renderCardContent(accessDenied: boolean) {
        if (accessDenied) {
            return this.renderAccessDeniedError();
        }

        return <>
            {this.getConfigError() ? <div className="mb-3">{this.renderErrorRepresentation()}</div> : null}

            <RenderIf expression={this.nowLoading()}>
                <div className="card card-body">
                    <div className="py-5 w-100 d-flex align-items-center justify-content-center text-muted">
                        <LoadingIndicator type="chase" className="me-3"/>
                        <Translate message="@common-phrases.loading"/>
                    </div>
                </div>
            </RenderIf>

            <RenderIf expression={this.getConfig()}>{config => {
                return <FileReceiver
                        controllerName={this.props.controllerName}
                        config={config}
                />;
            }}</RenderIf>
        </>;
    }

    protected getAccessPermissions(): string | string[] {
        const config = this.getConfig();
        if (config && config.permissions) {
            return config.permissions;
        }

        return app('names-resolver').permissions.getImportRoutePermissionName(this.props.controllerName);
    }

    protected getPageMetadataToUpdate() {
        const meta = super.getPageMetadataToUpdate();

        const listConfig = this.getListConfig();
        if (listConfig?.title) {
            meta.breadcrumbsItems = [
                {
                    label: listConfig.title,
                    uri: {
                        route: app('names-resolver')
                                .clientRoutes
                                .getIndexRouteName(this.props.controllerName),
                    },
                },
                ...meta.breadcrumbsItems,
            ];
        }

        return meta;
    }

    static getPageTitleByControllerName(controllerName: string): string | null {
        const config = ImportView.tryToGetConfigByControllerName<IImportConfig>(controllerName);
        if (!config || !config.title) {
            return null;
        }

        return config.title;
    }
}

export default ImportView.getStoreConnector()(ImportView);
